import { useEffect, useRef } from 'react';
import { useDecision } from '@optimizely/react-sdk';
import useAnalytics from '@/dataAnalytics/hooks/useAnalytics';
import { createPromo } from '@/dataAnalytics/dataAnalyticsHelper';
import { MerchandiseBanner as MerchandiseBannerContent } from '../types';

export type MerchandiseBannerPlacement = 'order-confirmation' | 'checkout' | 'home' | 'registration';

const placementToEventCategoryMap: Record<MerchandiseBannerPlacement, string> = {
  'order-confirmation': 'order confirmation',
  checkout: 'checkout my order',
  home: 'home',
  registration: 'registration form'
};

const placementToPromoNameMap: Record<MerchandiseBannerPlacement, string> = {
  home: 'Hut Hookup - Join HR Get Free Pizza',
  registration: 'registration form',
  checkout: 'checkout > checkout my order',
  'order-confirmation': 'checkout > order confirmation'
};

interface MerchandisingBannerAnalytics {
  trackClick: () => void;
}

export const useMerchandisingAnalytics = (merchandiseBanner: MerchandiseBannerContent, placement: MerchandiseBannerPlacement, merchandiseBannerEnabled?: boolean): MerchandisingBannerAnalytics => {
  const [{ enabled: merchandiseBannerTrackingEnabled }] = useDecision('dtg-1686_merchandising_banner_analytics');
  const analyticsEnabled = merchandiseBannerTrackingEnabled && merchandiseBannerEnabled;
  const analytics = useAnalytics();
  const impressionFiredRef = useRef(false);
  const eventCategory = placementToEventCategoryMap[placement];
  const promoName = placementToPromoNameMap[placement];
  const eventLabel = `merchandising - ${eventCategory}`;

  const promo = createPromo({
    promoName,
    promoType: 'merchandising',
    promoPosition: eventLabel,
    promoCreative: merchandiseBanner?.targetUrl,
    promoID: merchandiseBanner?.title
  });

  useEffect(() => {
    if (!analyticsEnabled || impressionFiredRef.current) return;
    impressionFiredRef.current = true;
    analytics.push(() => ({
      beacon_id: '9.02.02',
      event: 'promo_impression',
      event_category: eventCategory,
      event_action: `${eventLabel} > Promo Impression`,
      event_label: `${eventLabel} > Promo Impression`,
      promos: [promo]
    }));
  }, [analyticsEnabled, analytics, eventCategory, eventLabel, promo]);

  return {
    trackClick: () => {
      if (!analyticsEnabled) return;
      analytics.push(() => ({
        beacon_id: '9.04.02',
        event: 'promo_click',
        event_category: eventCategory,
        event_action: `${eventLabel} > Promo Click`,
        event_label: `${eventLabel} > Promo Click`,
        promos: [promo]
      }));
    }
  };
}